import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MobileBridgeService } from '@services/mobile-bridge.service';
import { MOBILE_BRIDGE_EVENT_SEND } from '@constants/mobile-bridge.constant';

@Injectable({
  providedIn: 'root'
})
export class DownloadService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly mbs: MobileBridgeService
  ) {}

  downloadCSV(fileName: string) {
    if (isPlatformBrowser(this.platformId)) {
      window.open(fileName);
    }
  }

  saveFile(data: string, filename: string, type = 'text/csv'): void {
    if (isPlatformBrowser(this.platformId)) {
      const blob = new Blob([data], { type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }

  async urlToDownloadFile(url: string): Promise<any> {
    if (this.mbs.isMobileApp) {
      this.mbs.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SET_DOWNLOAD_URL, { url });
      return;
    }
    if (isPlatformBrowser(this.platformId)) window.open(url, '_blank');
  }

  /**
   * Triggers download via direct link (no fetch/blob). Opens in new tab so the app is not replaced.
   * For cross-origin URLs to trigger download (not play), server should send Content-Disposition: attachment.
   */
  downloadFileViaLink(fileUrl: string, fileName: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.mbs.isMobileApp) {
      this.mbs.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SET_DOWNLOAD_URL, { url: fileUrl });
      return;
    }
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async downloadMedia(fileUrl: string, fileName: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.mbs.isMobileApp) {
      this.mbs.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SET_DOWNLOAD_URL, { url: fileUrl });
      return;
    }
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      this.downloadFileViaLink(fileUrl, fileName);
    }
  }

  async downloadFile(fileUrl: string, fileName: string) {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        console.error('Failed to download file:', response.statusText);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback: open in new tab if fetch fails
      window.open(fileUrl, '_blank');
    }
  }
  
}
